import { DateTime } from "luxon";
import { formatCompactDate } from "portal/utils/dates";
import { Paper, Skeleton, Typography } from "@mui/material";
import { titleCase } from "common/utils/strings";
import { useInfiniteScroll } from "portal/utils/hooks/useInfiniteScroll";
import { useTranslation } from "react-i18next";
import { useVersionHistory } from "./VersionHistoryProvider";
import { VersionCard, VersionMetadata } from "./VersionCard";
import i18n from "portal/i18n";
import React, { FC, useMemo } from "react";

export const VersionHistoryList: FC = () => {
  const { t } = useTranslation();
  const {
    allLoadedVersions,
    paginationState,
    loadMoreVersions,
    setSelectedVersionId,
    pathHighlight,
    setPathHighlight,
  } = useVersionHistory();

  const loadMoreRef = useInfiniteScroll<HTMLDivElement>(
    paginationState.loading,
    loadMoreVersions
  );

  const dateGroupedVersions = useMemo(() => {
    const dateGrouped: {
      [group: string]: { [subGroup: string]: VersionMetadata[] };
    } = {};
    if (allLoadedVersions.length === 0) {
      return {};
    }
    for (let i = 0; i < allLoadedVersions.length; i++) {
      const next =
        i + 1 <= allLoadedVersions.length
          ? allLoadedVersions[i + 1]
          : undefined;
      const version = allLoadedVersions[i]!;
      if (!version.activeTime) {
        continue;
      }
      const activeTime = DateTime.fromISO(version.activeTime);

      // high level group by day
      const groupKey: string = String(activeTime.startOf("day").toMillis());
      if (!dateGrouped[groupKey]) {
        dateGrouped[groupKey] = {};
      }

      // sub group by hour
      const subGroupKey = String(activeTime.hour);
      if (!dateGrouped[groupKey][subGroupKey]) {
        dateGrouped[groupKey][subGroupKey] = [];
      }

      dateGrouped[groupKey][subGroupKey].push({
        ...version,
        previousActiveVersionId: next?.versionId,
      });
    }
    return dateGrouped;
  }, [allLoadedVersions]);

  return (
    <Paper variant="section" className="p-2 pt-0 rounded-md">
      <Typography variant="body2" className="py-2">
        {titleCase(
          t("components.categoryCollectionProfile.history.historyListTitle")
        )}
      </Typography>
      {allLoadedVersions.length > 0 &&
        Object.entries(dateGroupedVersions)
          .sort(([dayA], [dayB]) => Number(dayB) - Number(dayA))
          .map(([day, hours]) => {
            const dayLabel = formatCompactDate(
              t,
              i18n,
              DateTime.fromMillis(Number(day)),
              false
            );
            return (
              <React.Fragment key={dayLabel}>
                {dayLabel && (
                  <Typography variant="body2" className="text-xs py-2">
                    {dayLabel}
                  </Typography>
                )}
                {Object.entries(hours)
                  .sort(([hourA], [hourB]) => Number(hourB) - Number(hourA))
                  .map(([hour, versions]) => (
                    <VersionCard
                      key={hour}
                      versions={versions}
                      highlight={pathHighlight}
                      onClick={(ccvId) => {
                        setSelectedVersionId(ccvId);
                        setPathHighlight(undefined);
                      }}
                    />
                  ))}
              </React.Fragment>
            );
          })}

      {paginationState.loading &&
        Array.from({ length: 5 })
          .fill(undefined)
          .map((_, i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              className="w-full h-24 my-1"
            />
          ))}
      {paginationState.nextCursor && (
        <div ref={loadMoreRef} style={{ height: "1px" }} />
      )}
    </Paper>
  );
};
